'use client';

import { InfiniteData } from '@tanstack/query-core';
import {
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { apiClient } from '@/shared/libs/apiClient';
import { PaginatedResponse } from '@/shared/types/common';
import { type TenantAccessState, writeBlockedMessage } from '@/shared/types/tenantAccess';

type HttpMethod = 'post' | 'put' | 'delete' | 'patch';

type UrlBuilder<D> = (vars: D) => string | { url: string; body?: unknown; init?: RequestInit };

type MaybeMeta = {
  meta?: {
    hasNext?: boolean;
    nextPage?: number | null;
    next_page?: number | null;
    page?: number;
  };
  page?: number;
};

export type InfiniteOpts<TPage, TKey extends readonly unknown[]> = Omit<
  UseInfiniteQueryOptions<TPage, Error, InfiniteData<TPage, number>, TKey, number>,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'select'
> & {
  initialPageParam?: number;
  buildUrl?: (pageParam: number) => string;
};

function defaultGetNextPageParam<TPage extends MaybeMeta>(
  lastPage: TPage,
  allPages: TPage[],
): number | null | undefined {
  if (lastPage?.meta?.hasNext === false) return undefined;
  if (lastPage?.meta?.nextPage != null) return lastPage.meta.nextPage;
  if (lastPage?.meta?.next_page != null) return lastPage.meta.next_page;

  const current = lastPage?.meta?.page ?? lastPage?.page ?? allPages.length;
  return current + 1;
}

function defaultBuildUrl(base: string, pageParam: number): string {
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}page=${pageParam ?? 1}`;
}

/* -------------------------- SECURE infinite -------------------------- */
export function useSecureInfiniteQuery<TPage, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  baseUrl: string,
  options?: InfiniteOpts<TPage, TKey>,
): UseInfiniteQueryResult<InfiniteData<TPage, number>, Error> {
  const buildUrl = options?.buildUrl ?? ((p: number) => defaultBuildUrl(baseUrl, p));

  return useInfiniteQuery<TPage, Error, InfiniteData<TPage, number>, TKey, number>({
    queryKey: key,
    initialPageParam: options?.initialPageParam ?? 1,
    queryFn: async ({ pageParam }) => {
      const url = buildUrl(pageParam);
      return apiClient.get<TPage>(url, true);
    },
    getNextPageParam:
      options?.getNextPageParam ??
      ((last, all) =>
        defaultGetNextPageParam(last as TPage & MaybeMeta, all as (TPage & MaybeMeta)[])),
    ...options,
  });
}

/**
 * Contrat d'une liste paginée : sa clé, sa route, et la façon d'en tourner les pages.
 *
 * Il existe pour être **partagé** entre le hook qui lit la liste et le préchargement qui la
 * remplit. Écrits chacun de leur côté, les deux avaient divergé : le préchargement écrivait
 * `QK.timesheets` avec `prefetchQuery` — donc `{ data, pagination }` — quand `useTimesheets` la
 * relit en requête infinie, qui attend `{ pages, pageParams }`. La lecture levait une exception
 * que la carte affichait telle quelle au consultant (#1881).
 *
 * Une seule description, deux usages : la forme ne peut plus dériver sans que les deux bougent.
 */
export type InfiniteQuerySpec<TPage, TKey extends readonly unknown[] = readonly unknown[]> = {
  key: TKey;
  base: string;
  options?: InfiniteOpts<TPage, TKey>;
};

/** Le hook de lecture d'une liste décrite par son contrat. */
export function useSecureInfiniteList<TPage, TKey extends readonly unknown[]>(
  spec: InfiniteQuerySpec<TPage, TKey>,
): UseInfiniteQueryResult<InfiniteData<TPage, number>, Error> {
  return useSecureInfiniteQuery<TPage, TKey>(spec.key, spec.base, spec.options);
}

/**
 * Le préchargement de la même liste — `prefetchInfiniteQuery`, jamais `prefetchQuery` : c'est le
 * seul qui dépose dans le cache la forme que la requête infinie sait relire.
 */
export async function securePrefetchInfiniteQuery<TPage, TKey extends readonly unknown[]>(
  qc: QueryClient,
  spec: InfiniteQuerySpec<TPage, TKey>,
) {
  const buildUrl = spec.options?.buildUrl ?? ((p: number) => defaultBuildUrl(spec.base, p));

  return qc.prefetchInfiniteQuery<TPage, Error, InfiniteData<TPage, number>, TKey, number>({
    queryKey: spec.key,
    initialPageParam: spec.options?.initialPageParam ?? 1,
    queryFn: ({ pageParam }) => apiClient.get<TPage>(buildUrl(pageParam), true),
    getNextPageParam:
      spec.options?.getNextPageParam ??
      ((last, all) =>
        defaultGetNextPageParam(last as TPage & MaybeMeta, all as (TPage & MaybeMeta)[])),
  });
}

/* -------------------------- PUBLIC infinite -------------------------- */
export function usePublicInfiniteQuery<TPage, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  baseUrl: string,
  options?: InfiniteOpts<TPage, TKey>,
): UseInfiniteQueryResult<InfiniteData<TPage, number>, Error> {
  const buildUrl = options?.buildUrl ?? ((p: number) => defaultBuildUrl(baseUrl, p));

  return useInfiniteQuery<TPage, Error, InfiniteData<TPage, number>, TKey, number>({
    queryKey: key,
    initialPageParam: options?.initialPageParam ?? 1,
    queryFn: async ({ pageParam }) => {
      const url = buildUrl(pageParam);
      return apiClient.get<TPage>(url, false);
    },
    getNextPageParam:
      options?.getNextPageParam ??
      ((last, all) =>
        defaultGetNextPageParam(last as TPage & MaybeMeta, all as (TPage & MaybeMeta)[])),
    ...options,
  });
}

/* -------------------------- SECURE prefetchQuery -------------------------- */
export async function securePrefetchQuery<T>(
  qc: QueryClient,
  key: readonly unknown[],
  url: string,
  options?: UseQueryOptions<T, Error>,
) {
  return qc.prefetchQuery<T>({
    queryKey: key,
    queryFn: () => apiClient.get<T>(url, true),
    ...options,
  });
}

/* -------------------------- SECURE query -------------------------- */
export function useSecureQuery<T, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  url: string,
  options?: Omit<UseQueryOptions<T, Error, T, TKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error, T, TKey>({
    queryKey: key,
    queryFn: () => apiClient.get<T>(url, true),
    ...options,
  });
}

/**
 * SECURE list query — tolère les deux formats de réponse renvoyés par l'API :
 * un tableau nu (`T[]`, ex. /admin/clients sans pagination) OU l'enveloppe
 * paginée `{ pagination, data }` (ex. /admin/accounts qui pagine toujours).
 * Retourne toujours `T[]`.
 */
export function useSecureListQuery<T, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  url: string,
  options?: Omit<UseQueryOptions<T[], Error, T[], TKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T[], Error, T[], TKey>({
    queryKey: key,
    queryFn: async () => {
      const res = await apiClient.get<T[] | PaginatedResponse<T>>(url, true);
      if (Array.isArray(res)) return res;
      return res?.data ?? [];
    },
    ...options,
  });
}

/**
 * Plafond de `limit` accepté par l'API (`PaginationQueryDto`). Demander plus
 * n'élargit pas la page : la requête est rejetée.
 */
const MAX_PAGE_SIZE = 200;

/**
 * Garde-fou : au-delà, on cesse de paginer et on le dit plutôt que d'enchaîner
 * les requêtes indéfiniment sur une réponse serveur inattendue.
 */
const MAX_PAGES = 25;

export type AllPages<T> = {
  items: T[];
  /** Total annoncé par l'API, ou `null` si l'endpoint ne pagine pas. */
  total: number | null;
  /** `true` si le plafond de pages a coupé la collecte : le jeu est incomplet. */
  truncated: boolean;
};

/**
 * SECURE — collecte **toutes** les pages d'un endpoint paginé.
 *
 * À réserver aux jeux dont un calcul dépend : un revenu, un décompte, un
 * classement. Un `limit` élevé sans `page` semble équivalent, mais ne l'est
 * pas — l'API plafonne `limit` à 200, et au-delà le reste est simplement
 * absent sans que rien ne le signale. Sur une somme, l'absence se lit comme un
 * montant plus faible, pas comme une donnée manquante.
 *
 * Pour **afficher une liste**, ce hook est le mauvais outil : c'est une
 * pagination serveur qu'il faut, page par page.
 */
/** Collecte toutes les pages d'un endpoint paginé. Voir `useSecureAllPagesQuery`. */
async function fetchAllPages<T>(baseUrl: string): Promise<AllPages<T>> {
  const pageUrl = (page: number) => {
    const joiner = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${joiner}page=${page}&limit=${MAX_PAGE_SIZE}`;
  };

  const first = await apiClient.get<T[] | PaginatedResponse<T>>(pageUrl(1), true);

  // Endpoint non paginé : le tableau nu est déjà complet.
  if (Array.isArray(first)) {
    return { items: first, total: first.length, truncated: false };
  }

  const total = first?.pagination?.total ?? null;
  const last = first?.pagination?.last ?? 1;
  const items = first?.data ?? [];
  if (last <= 1) return { items, total, truncated: false };

  // Le nombre de pages est connu dès la première : les suivantes partent
  // ensemble plutôt qu'en chaîne, où la latence s'additionnerait.
  const capped = Math.min(last, MAX_PAGES);
  const rest = await Promise.all(
    Array.from({ length: capped - 1 }, (_, i) =>
      apiClient.get<PaginatedResponse<T>>(pageUrl(i + 2), true),
    ),
  );

  return {
    items: [...items, ...rest.flatMap(page => page?.data ?? [])],
    total,
    truncated: last > MAX_PAGES,
  };
}

export function useSecureAllPagesQuery<T, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  baseUrl: string,
  options?: Omit<UseQueryOptions<AllPages<T>, Error, AllPages<T>, TKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<AllPages<T>, Error, AllPages<T>, TKey>({
    queryKey: key,
    queryFn: () => fetchAllPages<T>(baseUrl),
    ...options,
  });
}

/**
 * SECURE — toutes les pages, rendues comme un simple tableau.
 *
 * Même collecte que `useSecureAllPagesQuery`, avec la signature de
 * `useSecureListQuery` : c'est le remplacement direct d'un `?limit=100` sans
 * `page`, sans toucher aux écrans qui consomment le tableau.
 *
 * La troncature du garde-fou n'est pas remontée ici. À réserver donc aux écrans
 * où le plafond de 25 pages est hors de portée ; au-delà, prendre
 * `useSecureAllPagesQuery` et afficher `truncated`.
 */
export function useSecureAllPagesListQuery<T, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  baseUrl: string,
  options?: Omit<UseQueryOptions<T[], Error, T[], TKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T[], Error, T[], TKey>({
    queryKey: key,
    queryFn: async () => (await fetchAllPages<T>(baseUrl)).items,
    ...options,
  });
}

/* -------------------------- PUBLIC query -------------------------- */
export function usePublicQuery<T, TKey extends readonly unknown[] = readonly unknown[]>(
  key: TKey,
  url: string,
  options?: Omit<UseQueryOptions<T, Error, T, TKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error, T, TKey>({
    queryKey: key,
    queryFn: () => apiClient.get<T>(url, false),
    ...options,
  });
}

/* -------------------------- SECURE mutation -------------------------- */
/**
 * L'écriture est-elle refusée par l'état d'accès du tenant ?
 *
 * Lu **depuis le cache** de `/auth/me` plutôt que par le hook dédié : importer
 * `useTenantAccess` ici créerait un cycle avec ce module, exactement le genre de
 * cycle qui ne se voit qu'au démarrage.
 *
 * Cette garde ne remplace pas celle du backend, qui reste la seule autorité. Elle
 * évite l'aller-retour et surtout le message générique : refuser tout de suite,
 * en disant pourquoi, vaut mieux qu'un 403 après une saisie complète.
 */
function useWriteBlock(): () => string | null {
  const queryClient = useQueryClient();
  return () => {
    const me = queryClient.getQueryData<{ access?: TenantAccessState | null }>(['users', 'me']);
    return writeBlockedMessage(me?.access);
  };
}

export function useSecureMutation<T, D = unknown, C = unknown>(
  key: readonly unknown[],
  method: HttpMethod,
  urlOrBuilder: string | UrlBuilder<D>,
  options?: UseMutationOptions<T, Error, D, C>,
) {
  const writeBlock = useWriteBlock();

  return useMutation<T, Error, D, C>({
    mutationKey: key,
    mutationFn: (vars: D) => {
      const blocked = writeBlock();
      if (blocked) return Promise.reject(new Error(blocked));

      const resolved =
        typeof urlOrBuilder === 'function' ? urlOrBuilder(vars) : { url: urlOrBuilder };

      const url = typeof resolved === 'string' ? resolved : resolved.url;
      const body =
        typeof resolved === 'string' ? (vars as unknown) : (resolved.body ?? (vars as unknown));
      const init = typeof resolved === 'string' ? undefined : resolved.init;

      return apiClient[method]<T>(url, body, true, init);
    },
    ...options,
  });
}

/* -------------------------- PUBLIC mutation -------------------------- */
export function usePublicMutation<T, D = unknown, C = unknown>(
  key: readonly unknown[],
  method: HttpMethod,
  urlOrBuilder: string | UrlBuilder<D>,
  options?: UseMutationOptions<T, Error, D, C>,
) {
  return useMutation<T, Error, D, C>({
    mutationKey: key,
    mutationFn: (vars: D) => {
      const publicResolvedUrl =
        typeof urlOrBuilder === 'function' ? urlOrBuilder(vars) : { url: urlOrBuilder };

      const url = typeof publicResolvedUrl === 'string' ? publicResolvedUrl : publicResolvedUrl.url;
      const body =
        typeof publicResolvedUrl === 'string'
          ? (vars as unknown)
          : (publicResolvedUrl.body ?? (vars as unknown));
      const init = typeof publicResolvedUrl === 'string' ? undefined : publicResolvedUrl.init;

      return apiClient[method]<T>(url, body, false, init);
    },
    ...options,
  });
}
